import { useState, useEffect, useRef, ReactNode } from "react";

interface LazySectionProps {
  children: ReactNode;
  /** Optional ID for the section - renders immediately for anchor navigation */
  id?: string;
  /** Root margin for intersection observer (default: "200px") */
  rootMargin?: string;
  /** Minimum height placeholder while loading (default: "400px") */
  minHeight?: string;
  /** Class name for the wrapper */
  className?: string;
}

/**
 * LazySection - Defers rendering of children until they're near the viewport.
 * Uses Intersection Observer to detect when the section is approaching view.
 * Improves initial page load by not rendering below-the-fold content immediately.
 * 
 * IMPORTANT: The `id` prop is always rendered on the wrapper div, allowing
 * browser hash navigation to scroll to the section even before children load.
 */
const LazySection = ({
  children,
  id,
  rootMargin = "200px",
  minHeight = "400px",
  className = "",
}: LazySectionProps) => {
  const [isVisible, setIsVisible] = useState(false);
  const [hasLoaded, setHasLoaded] = useState(false);
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const element = sectionRef.current;
    if (!element) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          setHasLoaded(true);
          observer.disconnect();
        }
      },
      {
        rootMargin,
        threshold: 0,
      }
    );

    observer.observe(element);

    return () => observer.disconnect();
  }, [rootMargin]);

  // Once loaded, always render children (don't unload on scroll away)
  if (hasLoaded) {
    return <div id={id} className={className}>{children}</div>;
  }

  return (
    <div
      ref={sectionRef}
      id={id}
      className={className}
      style={{ minHeight }}
    >
      {isVisible && children}
    </div>
  );
};

export default LazySection;
